// Procurement design tokens — single source of truth for colour, type, space,
// and status semantics. CSS variables live in tokens.css; this module is for
// JS (inline styles, StatusPill maps, KPI thresholds).

export const COLOR = {
  bgApp: "#F7F8FA",
  bgSurface: "#FFFFFF",
  bgSubtle: "#F1F3F7",
  border: "#E2E6ED",
  borderStrong: "#C8CFDA",
  textPrimary: "#101828",
  textSecondary: "#5A6478",
  textMuted: "#8A93A5",
  brand600: "#3B45C4",
  brand500: "#4F5AE0",
  brand50: "#EEF0FF",
  success600: "#0E7A57",
  success50: "#E6F5F0",
  warning600: "#B45309",
  warning50: "#FEF3E2",
  info600: "#1D4ED8",
  info50: "#E8EFFE",
  danger600: "#B42318",
  danger50: "#FEECEA",
  neutral600: "#475467",
  neutral50: "#F2F4F7",
}

export const RADIUS = {
  control: 6,
  panel: 10,
}

export const TYPE = {
  display: 28,
  title: 22,
  section: 18,
  lead: 15,
  body: 14,
  small: 13,
  caption: 12,
}

export const SPACE = 4

export const TABLE_PAD = {
  comfortable: "10px 14px",
  compact: "6px 14px",
}

// Semantic pill map. Background is always the `-50` tint; foreground is `-600`.
// Never use a saturated fill for status — those read as clickable CTAs.
export const STATUS_TONE = {
  draft: "neutral",
  new: "neutral",
  pending: "info",
  info_requested: "warning",
  approved: "success",
  rejected: "danger",
  closed: "neutral",
  issued: "success",
  receiving: "info",
  completed: "success",
  cancelled: "danger",
  sent_to_accounting: "info",
  unpaid: "warning",
  partial: "warning",
  paid: "success",
  match: "success",
  mismatch: "danger",
  kyc_pending: "warning",
  kyc_verified: "success",
  kyc_rejected: "danger",
  over_budget: "danger",
  active: "success",
  inactive: "neutral",
  urgent: "danger",
  normal: "neutral",
}

export const TONE_COLORS = {
  success: { fg: COLOR.success600, bg: COLOR.success50 },
  warning: { fg: COLOR.warning600, bg: COLOR.warning50 },
  info: { fg: COLOR.info600, bg: COLOR.info50 },
  danger: { fg: COLOR.danger600, bg: COLOR.danger50 },
  neutral: { fg: COLOR.neutral600, bg: COLOR.neutral50 },
}

export const STATUS_LABEL = {
  draft: "Draft",
  new: "New",
  pending: "Waiting for approval",
  info_requested: "Info requested",
  approved: "Approved",
  rejected: "Rejected",
  closed: "Closed",
  issued: "Issued",
  receiving: "Receiving",
  completed: "Completed",
  cancelled: "Cancelled",
  sent_to_accounting: "Sent to accounting",
  unpaid: "Unpaid",
  partial: "Partial",
  paid: "Paid",
  match: "Match",
  mismatch: "Mismatch",
  kyc_pending: "KYC pending",
  kyc_verified: "KYC verified",
  kyc_rejected: "KYC rejected",
  over_budget: "Over budget",
  active: "Active",
  inactive: "Inactive",
  urgent: "Urgent",
  normal: "Normal",
}

export const toneOf = (status) => STATUS_TONE[status] || "neutral"

export const statusStyle = (status) => TONE_COLORS[toneOf(status)]

export const statusLabel = (status) =>
  STATUS_LABEL[status] || (status ? String(status).replace(/_/g, " ") : "—")

// Urgency/priority — kept separate from STATUS_TONE so a future "critical"
// or "high" level (should the backend add one) doesn't collide with an
// unrelated status key. Today the API only emits normal|urgent.
export const URGENCY_TONE = {
  low: "neutral",
  normal: "neutral",
  high: "warning",
  critical: "danger",
  urgent: "danger",
}

export const URGENCY_LABEL = {
  low: "Low",
  normal: "Normal",
  high: "High",
  critical: "Critical",
  urgent: "Urgent",
}

export const urgencyToneOf = (urgency) => URGENCY_TONE[String(urgency || "").toLowerCase()] || "neutral"

export const urgencyStyle = (urgency) => TONE_COLORS[urgencyToneOf(urgency)]

export const urgencyLabel = (urgency) =>
  URGENCY_LABEL[String(urgency || "").toLowerCase()] || (urgency ? String(urgency) : "—")

// Quiet levels don't need the attention-grabbing dot — only a level that
// should stand out in a dense table (high/critical/urgent) gets one.
export const urgencyIsQuiet = (urgency) => !["high", "critical", "urgent"].includes(String(urgency || "").toLowerCase())

// Budget utilisation thresholds (consumed / total).
export const budgetStatusOf = (usedPct) => {
  if (usedPct > 100) return { key: "over", tone: "danger", label: "Over budget" }
  if (usedPct > 90) return { key: "critical", tone: "danger", label: "Critical" }
  if (usedPct >= 70) return { key: "watch", tone: "warning", label: "Watch" }
  return { key: "healthy", tone: "neutral", label: "On track" }
}

export const remainingAlert = (remaining, total) => {
  if (!total || total <= 0) return { key: "na", tone: "neutral" }
  const ratio = remaining / total
  if (ratio < 0.1) return { key: "critical", tone: "danger" }
  if (ratio < 0.2) return { key: "low", tone: "warning" }
  return { key: "ok", tone: "success" }
}
